import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../api";
import { ShoppingCart, Sparkles, MapPin, Phone, ChevronDown } from "lucide-react";
import { RedemptionHistory } from "../RedemptionHistory";

export const DEFAULT_SECTIONS = [
  { key: "products", enabled: true }, { key: "about", enabled: false }, { key: "reactions", enabled: true },
  { key: "faq", enabled: false }, { key: "hours", enabled: false }, { key: "balance", enabled: true }, { key: "corporate", enabled: true },
];

export const useGoogleFonts = (fonts) => {
  const list = [...new Set(fonts.filter(Boolean))];
  const key = list.join("|");
  useEffect(() => {
    if (!key) return;
    const href = `https://fonts.googleapis.com/css2?${key.split("|").map((f) => `family=${f.replace(/ /g, "+")}:wght@400;600;700`).join("&")}&display=swap`;
    const link = document.createElement("link");
    link.rel = "stylesheet"; link.href = href; link.dataset.storeFonts = "1";
    document.head.appendChild(link);
    return () => link.remove();
  }, [key]);
};

export const Panel = ({ glass, children, testid }) => (
  <div className="max-w-[1100px] mx-auto px-6 pb-12" data-testid={testid}>
    <div className={glass ? "bg-white/90 backdrop-blur-md rounded-3xl p-6 sm:p-8 shadow-[0_10px_30px_rgba(20,30,55,0.10)]" : ""}>{children}</div>
  </div>
);

const HERO_PAD = { short: "py-12", medium: "py-20", tall: "py-32 sm:py-40" };

export const StoreHero = ({ b, d, brand, hf }) => {
  const layout = d.hero_layout || "centered";
  const pad = HERO_PAD[d.hero_height || "medium"];
  const text = (
    <div className={layout === "centered" ? "text-center" : "text-left"}>
      {b.logo_url && <img src={b.logo_url} alt={b.name} className={`h-24 sm:h-32 mb-6 object-contain ${layout === "centered" ? "mx-auto" : ""}`} />}
      <h1 className="text-white text-4xl sm:text-5xl font-bold" style={hf}>{b.name}</h1>
      <p className={`text-white/85 text-[1.15rem] mt-3 max-w-[600px] ${layout === "centered" ? "mx-auto" : ""}`}>{b.description}</p>
    </div>
  );
  if (layout === "split") return (
    <div className="grid md:grid-cols-2" style={{ background: brand }} data-testid="store-hero-split">
      <div className={`px-6 sm:px-12 ${pad} flex items-center`}>{text}</div>
      {b.hero_image ? <div className="relative min-h-[260px]"><img src={b.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover" /></div> : <div className="min-h-[260px] bg-black/10" />}
    </div>
  );
  return (
    <div className="relative" style={{ background: brand }} data-testid={`store-hero-${layout}`}>
      {b.hero_image && <img src={b.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
      <div className={`relative max-w-[1100px] mx-auto px-6 ${pad}`}>{text}</div>
    </div>
  );
};

const CORNERS = { rounded: "rounded-2xl", square: "rounded-none", pill: "rounded-[2rem]" };
const COLS = { 2: "lg:grid-cols-2", 3: "lg:grid-cols-3", 4: "lg:grid-cols-4" };

export const ProductGrid = ({ products, slug, d, colors, hf }) => {
  const corner = CORNERS[d.card_corners || "rounded"];
  return (
    <>
      <h2 className="text-[1.8rem] font-bold mb-8" style={{ color: colors.text, ...hf }}>Browse our gift vouchers</h2>
      {products.length === 0 ? <p className="text-[#5a6b80]">No vouchers available yet. Please check back soon.</p> : (
        <div className={`grid sm:grid-cols-2 ${COLS[d.card_columns || 3]} gap-6`} data-testid="store-product-grid">
          {products.map((p) => (
            <Link key={p.id} to={`/store/${slug}/vouchers/${p.slug}`} data-testid={`store-product-${p.slug}`}
              className={`bg-white ${corner} overflow-hidden shadow-[0_10px_30px_rgba(20,30,55,0.07)] hover:shadow-[0_18px_40px_rgba(20,30,55,0.13)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 flex flex-col`}>
              <div className="aspect-square bg-gray-100 overflow-hidden relative">
                {p.image_url ? <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center" style={{ background: colors.brand }}><ShoppingCart className="text-white/70" size={36} /></div>}
                {p.kind === "experience" && <span className="absolute top-3 left-3 bg-white/95 text-[#16243b] text-xs font-semibold px-3 py-1 rounded-full inline-flex items-center gap-1" data-testid="experience-badge"><Sparkles size={12} /> Experience</span>}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-semibold text-[1.1rem]" style={{ color: colors.text, ...hf }}>{p.title}</h3>
                {d.card_show_description !== false && <p className="text-[#5a6b80] text-sm mt-1 flex-1">{p.kind === "experience" && p.experience_details ? p.experience_details : p.description}</p>}
                <div className="flex items-center justify-between gap-2 mt-4">
                  <span className="text-[1.4rem] font-bold" style={{ color: colors.text }}>£{p.price.toFixed(2)}</span>
                  <span className="rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap" style={{ background: colors.btnBg, color: colors.btnText }}>View &amp; buy</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </>
  );
};

export const AboutSection = ({ about, colors, hf }) => (
  <div className={`grid ${about.image ? "md:grid-cols-2" : ""} gap-8 items-center`} data-testid="store-about">
    {about.image && <img src={about.image} alt="" className="w-full aspect-[4/3] object-cover rounded-2xl" />}
    <div>
      <h2 className="text-[1.8rem] font-bold" style={{ color: colors.text, ...hf }}>{about.title || "About us"}</h2>
      <p className="text-[#5a6b80] leading-relaxed mt-3 whitespace-pre-line">{about.text}</p>
    </div>
  </div>
);

export const FaqSection = ({ faqs, colors, hf }) => {
  const [open, setOpen] = useState(0);
  return (
    <div data-testid="store-faq">
      <h2 className="text-[1.8rem] font-bold mb-5" style={{ color: colors.text, ...hf }}>Questions & answers</h2>
      <div className="divide-y divide-gray-200 bg-white rounded-2xl px-5">
        {faqs.map((f, i) => (
          <div key={i}>
            <button onClick={() => setOpen(open === i ? -1 : i)} data-testid={`store-faq-q-${i}`} className="w-full flex justify-between items-center py-4 text-left font-medium" style={{ color: colors.text }}>
              {f.q}<ChevronDown size={18} className={`transition-transform duration-200 shrink-0 ${open === i ? "rotate-180" : ""}`} />
            </button>
            {open === i && <p className="text-[#5a6b80] text-sm pb-4 whitespace-pre-line">{f.a}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

export const HoursSection = ({ d, colors, hf }) => (
  <div className="grid md:grid-cols-2 gap-8" data-testid="store-hours">
    {d.hours?.length > 0 && (
      <div>
        <h2 className="text-[1.8rem] font-bold mb-4" style={{ color: colors.text, ...hf }}>Opening hours</h2>
        <dl className="bg-white rounded-2xl p-5 space-y-2 text-sm">
          {d.hours.map((h, i) => <div key={i} className="flex justify-between"><dt className="font-medium" style={{ color: colors.text }}>{h.day}</dt><dd className="text-[#5a6b80]">{h.hours}</dd></div>)}
        </dl>
      </div>
    )}
    {(d.address || d.phone) && (
      <div>
        <h2 className="text-[1.8rem] font-bold mb-4" style={{ color: colors.text, ...hf }}>Find us</h2>
        <div className="bg-white rounded-2xl p-5 space-y-3 text-sm text-[#5a6b80]">
          {d.address && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(d.address)}`} target="_blank" rel="noreferrer" className="flex gap-2 hover:text-[#16243b]" data-testid="store-address"><MapPin size={18} className="shrink-0" style={{ color: colors.btnBg }} /><span className="whitespace-pre-line">{d.address}</span></a>}
          {d.phone && <a href={`tel:${d.phone}`} className="flex gap-2 hover:text-[#16243b]"><Phone size={18} style={{ color: colors.btnBg }} />{d.phone}</a>}
        </div>
      </div>
    )}
  </div>
);

export const BalanceSection = ({ slug, b, colors, hf }) => {
  const [code, setCode] = useState("");
  const [bal, setBal] = useState(null);
  const [err, setErr] = useState("");
  const check = async () => {
    setErr(""); setBal(null);
    if (!code.trim()) return;
    try { setBal((await api.get(`/store/${slug}/voucher/${code.trim()}`)).data); } catch { setErr("No voucher found with that code for this store."); }
  };
  const exp = bal?.kind === "experience";
  return (
    <div className="bg-white rounded-2xl shadow-[0_10px_30px_rgba(20,30,55,0.07)] p-6" data-testid="store-check-balance">
      <h2 className="text-[1.4rem] font-bold mb-1" style={{ color: colors.text, ...hf }}>Check a voucher balance</h2>
      <p className="text-[#5a6b80] text-sm mb-4">Enter a {b.name} voucher code to see its remaining balance.</p>
      <div className="flex flex-col sm:flex-row gap-3">
        <input value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setErr(""); setBal(null); }} placeholder="e.g. ABCD123456" data-testid="store-balance-input"
          className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] font-mono" />
        <button onClick={check} data-testid="store-balance-btn" className="rounded-xl px-6 py-2.5 font-medium" style={{ background: colors.btnBg, color: colors.btnText }}>Check balance</button>
      </div>
      {err && <p className="text-red-500 text-sm mt-3" data-testid="store-balance-error">{err}</p>}
      {bal && (
        <div className="mt-4 bg-[#eefafa] rounded-xl p-4" data-testid="store-balance-result">
          <p className="text-[#5a6b80] text-sm">{bal.product_title}</p>
          <p className="text-2xl font-bold" style={{ color: colors.text }}>
            {exp ? (bal.balance > 0 ? "Experience not yet used" : "Experience used") : <>£{bal.balance.toFixed(2)} <span className="text-sm font-normal text-[#8a97a8]">remaining of £{bal.original_amount.toFixed(2)}</span></>}
          </p>
          <p className="text-xs text-[#8a97a8] mt-1">{bal.expired ? "This voucher has expired." : bal.expires_at ? `Valid until ${bal.expires_at.slice(0, 10)}` : ""}</p>
          <RedemptionHistory redemptions={bal.redemptions} textColor={colors.text} experience={exp} />
        </div>
      )}
    </div>
  );
};

export const CorporateCTA = ({ slug, colors, hf }) => (
  <Link to={`/store/${slug}/corporate`} data-testid="store-corporate-link"
    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl p-6 bg-white shadow-[0_10px_30px_rgba(20,30,55,0.07)] hover:-translate-y-0.5 transition-transform duration-300">
    <span><span className="block font-bold text-[1.2rem]" style={{ color: colors.text, ...hf }}>Buying for a team or clients?</span>
      <span className="text-sm text-[#5a6b80]">Order lots of vouchers at once and pay by invoice.</span></span>
    <span className="rounded-full px-5 py-2 text-sm font-medium shrink-0" style={{ background: colors.btnBg, color: colors.btnText }}>Corporate gifting →</span>
  </Link>
);
